import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  actionsColumn,
  dateColumn,
  priorityColumn,
  statusColumn,
  textColumn,
  type ColumnDef,
} from '@/components/ui'
import { customerApi } from '../lib/api'

interface TicketRow {
  id: number
  nr: string | null
  subject: string
  status: string
  priority: string
  createdAt: string | null
  dueDate: string | null
}

export function CustomerTicketsPage() {
  const [rows, setRows] = useState<TicketRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    customerApi.get<{ data: TicketRow[] }>('/tickets')
      .then((res) => setRows(res.data?.data ?? []))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  const columns = useMemo<ColumnDef<TicketRow, unknown>[]>(
    () => [
      {
        accessorKey: 'nr',
        header: 'Nr.',
        cell: ({ row }) => row.original.nr ?? row.original.id,
      },
      textColumn<TicketRow>('subject', 'Betreff'),
      statusColumn<TicketRow>('status'),
      priorityColumn<TicketRow>('priority'),
      dateColumn<TicketRow>('createdAt', 'Erstellt'),
      actionsColumn<TicketRow>('', (row) => (
        <Button variant="link" size="sm" onClick={() => navigate(`/tickets/${row.id}`)}>
          Öffnen
        </Button>
      )),
    ],
    [navigate],
  )

  return (
    <div>
      <PageHeader
        title="Tickets"
        description="Alle offenen und abgeschlossenen Anfragen."
        actions={<Button variant="primary" onClick={() => navigate('/tickets/neu')}>Neues Ticket</Button>}
      />
      {loading && <LoadingState />}
      {error && <ErrorState details={error} />}
      {!loading && !error && (
        <Card>
          <Card.Body>
            <DataTable<TicketRow>
              data={rows}
              columns={columns}
              getRowId={(r) => String(r.id)}
              emptyState={
                <EmptyState
                  title="Keine Tickets"
                  description="Du hast aktuell keine Tickets."
                  action={
                    <Button variant="primary" onClick={() => navigate('/tickets/neu')}>
                      Neues Ticket anlegen
                    </Button>
                  }
                />
              }
            />
          </Card.Body>
        </Card>
      )}
    </div>
  )
}
